/**
 * MODELO DE VIEWMODEL DE PÁGINA DE CADASTRO.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { CentroCustoForm, CentroCustoSchema, defaultCentroCustoForm } from "../schemas/CentroCusto_cadastros/centroCusto";
import { zodResolver } from "@hookform/resolvers/zod";
import { CentroCusto } from "../../domain/models/centro_custo";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";
import { defaultInformacaoFaturamentoForm, InformacaoFaturamentoForm, InformacaoFaturamentoSchema } from "../schemas/CentroCusto_cadastros/informacaoFaturamento";
import { InformacaoFaturamento } from "../../domain/models/informacao_faturamento";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { usePagination } from "../hooks/centro_custo_cadastro/pagination";
import { CentroCustoEndereco } from "../../domain/models/centro_custo_endereco";
import { CentroCustoEnderecoForm, CentroCustoEnderecoSchema, defaultCentroCustoEnderecoForm } from "../schemas/CentroCusto_cadastros/centroCustoEndereco";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";


export const useCentroCustoCadastroViewModel = () => {

    const {
        rh: {
            getCepUseCase
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getClientesDropDownUseCase,
        },
        comercial: {

            getCentroCustoUseCase,
            deleteCentroCustoByIdUseCase,
            updateCentroCustoUseCase,
            createCentroCustoUseCase,
            getAllInformacaoFaturamentoUseCase,
            getInformacaoFaturamentoUseCase,
            createInformacaoFaturamentoUseCase,
            updateInformacaoFaturamentoUseCase,
            deleteInformacaoFaturamentoUseCase,
            getClienteByIdUseCase,
            createCentroCustoEnderecoUseCase,
            deleteCentroCustoEnderecoUseCase,
            getAllCentroCustoEnderecoUseCase,
            getCentroCustoEnderecoUseCase,
            updateCentroCustoEnderecoUseCase,

        } } = useDependencyInjection()

    const location = useLocation();
    const navigate = useNavigate();
    const pagination = usePagination();

    const menuRef = useRef<HTMLButtonElement>(null);
    const [openMenu, setOpenMenu] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [openInformacaoFaturamentoModal, setOpenInformacaoFaturamentoModal] = useState(false);
    const [openCentroCustoEnderecoModal, setOpenCentroCustoEnderecoModal] = useState(false);


    const [openCentroCustoDeleteModal, setOpenCentroCustoDeleteModal] = useState(false);
    const [openInformacaoFaturamentoDeleteModal, setOpenInformacaoFaturamentoDeleteModal] = useState(false);
    const [openCentroCustoEnderecoDeleteModal, setOpenCentroCustoEnderecoDeleteModal] = useState(false);

    const [selectedIdCentroCustoDelete, setSelectedIdCentroCustoDelete] = useState<number>();
    const [selectedIdInformacaoFaturamentoDelete, setSelectedIdInformacaoFaturamentoDelete] = useState<number>();
    const [selectedIdCentroCustoEnderecoDelete, setselectedIdCentroCustoEnderecoDelete] = useState<number>();

    const debouncedRef = useCreateDebounceHandler(750);

    const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();

    const [listDropDownClientes, setListDropDownClientes] = useState<ClienteDropDown[]>();
    const [selectedClienteDropDown, setSelectedClienteDropDown] = useState<ClienteDropDown>();

    const [informacaoFaturamentolist, setInformacaoFaturamentoList] = useState<InformacaoFaturamento[]>([]);
    const [centroCustoEnderecoList, setCentroCustoEnderecoList] = useState<CentroCustoEndereco[]>([]);

    const [selectedCentroCustoEndereco, setSelectedCentroCustoEndereco] = useState<CentroCustoEndereco>();
    const [selectedCentroCusto, setSelectedCentroCusto] = useState<CentroCusto>();
    const [selectedInformacaoFaturamento, setSelectedInformacaoFaturamento] = useState<InformacaoFaturamento>();


    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const hookFormFaturamento = useForm<InformacaoFaturamentoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultInformacaoFaturamentoForm,
        resolver: zodResolver(InformacaoFaturamentoSchema),

    });

    const hookFormEndereco = useForm<CentroCustoEnderecoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultCentroCustoEnderecoForm,
        resolver: zodResolver(CentroCustoEnderecoSchema),

    });

    const hookForm = useForm<CentroCustoForm>({
        reValidateMode: 'onSubmit',
        mode: 'onSubmit',
        defaultValues: defaultCentroCustoForm,
        resolver: zodResolver(CentroCustoSchema),
    });


    const loadCentroCustoSelecionado = useCallback(async (idCentroCusto: number | undefined) => {
        try {
            if (idCentroCusto) {

                const centroCusto = await getCentroCustoUseCase.getCentroCustoByid(idCentroCusto)
                hookForm.setValue('idCentroCusto', idCentroCusto);
                hookForm.setValue('status', centroCusto.status ?? 0);
                hookForm.setValue('codigo', centroCusto.codigo ?? '');
                hookForm.setValue('descricao', centroCusto.descricao ?? '');
                hookForm.setValue('bdi', centroCusto.bdi ?? null);
                hookForm.setValue('bdi2', centroCusto.bdi2 ?? null);
                hookForm.setValue('numeroCno', centroCusto.numeroCno ?? '');
                hookForm.setValue('obsCentroCusto', centroCusto.obsCentrocusto ?? '');
                setSelectedCentroCusto(centroCusto);
            } else {
                hookForm.reset()
            }
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }

    }, [
        hookForm,
        getCentroCustoUseCase,

    ])

    const loadAllCentroCustoEndereco = useCallback(async (params?: { id?: number, page?: number, rowsPerPage?: number }) => {
        try {
            const id = params?.id ?? selectedCentroCusto?.idCentroCusto;

            if (id) {
                const page = params?.page ?? pagination.centroCustoEnderecos.page;
                const rowsPerPage = params?.rowsPerPage ?? pagination.centroCustoEnderecos.rowsPerPage;

                await getAllCentroCustoEnderecoUseCase.getAllCentroCustoEndereco(
                    page,
                    rowsPerPage,
                    id,
                );
                setCentroCustoEnderecoList(getAllCentroCustoEnderecoUseCase.items ?? [])

                usePagination.getState().centroCustoEnderecosSetter.setRowsPerPage(getAllCentroCustoEnderecoUseCase.pageSize);
                usePagination.getState().centroCustoEnderecosSetter.setTotalItems(getAllCentroCustoEnderecoUseCase.totalItems);
            }
        } catch (e: unknown) {
            setCentroCustoEnderecoList([]);
            throw new Error("Falha ao carregar outros endereços!");
        }
    }, [
        pagination,
        getAllCentroCustoEnderecoUseCase,
        selectedCentroCusto,
    ]);


    const loadAllInformacaoFaturamento = useCallback(async (params?: { id?: number, page?: number, rowsPerPage?: number }) => {
        try {
            const id = params?.id ?? selectedCentroCusto?.idCentroCusto;
            if (id) {
                const page = params?.page ?? pagination.informacaoFaturamentos.page;
                const rowsPerPage = params?.rowsPerPage ?? pagination.informacaoFaturamentos.rowsPerPage;

                await getAllInformacaoFaturamentoUseCase.getAllinformacaoFaturamento(
                    page,
                    rowsPerPage,
                    id,
                );
                setInformacaoFaturamentoList(getAllInformacaoFaturamentoUseCase.items ?? [])

                usePagination.getState().informacaoFaturamentosSetter.setRowsPerPage(getAllInformacaoFaturamentoUseCase.pageSize);
                usePagination.getState().informacaoFaturamentosSetter.setTotalItems(getAllInformacaoFaturamentoUseCase.totalItems);
            }
        } catch (e: unknown) {
            setInformacaoFaturamentoList([]);
            throw new Error("Falha ao carregar controle de patrimonios!");
        }
    }, [
        pagination,
        getAllInformacaoFaturamentoUseCase,
        selectedCentroCusto,
    ]);

    useLayoutEffect(() => {
        setPageLoading(true)
    }, [setPageLoading])

    usePageStartBootstrap(async () => {
        try {

            const idCentroCusto = selectedCentroCusto?.idCentroCusto ?? location.state?.idCentroCusto as number
            if (idCentroCusto) {
                await loadCentroCustoSelecionado(idCentroCusto)
                await loadAllInformacaoFaturamento({ id: idCentroCusto, rowsPerPage: 5, page: 1 })
                await loadAllCentroCustoEndereco({ id: idCentroCusto, rowsPerPage: 5, page: 1 })
            }

            await handleGetClientesDropDownList();

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar página!'));
        }
    })

    // FUNÇÕES CENTRO DE CUSTO

    const handleCloseMenu = useCallback((event: any) => {
        if (
            menuRef.current &&
            menuRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpenMenu(false);
    }, [menuRef]);

    const handleToggleMenu = useCallback(() => {
        setOpenMenu((prev) => !prev);
    }, []);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenMenu(false);
        } else if (event.key === 'Escape') {
            setOpenMenu(false);
        }
    }, []);

    const handleNewCentroCusto = useCallback(() => {
        setSelectedCentroCusto(undefined);
        setSelectedCentroCustoEndereco(undefined);
        setSelectedInformacaoFaturamento(undefined);
        setCentroCustoEnderecoList([])
        setInformacaoFaturamentoList([])
        setSelectedClienteDropDown(undefined);

        hookForm.reset();
        hookFormEndereco.reset();
        hookFormFaturamento.reset();

    }, [hookForm, hookFormEndereco, hookFormFaturamento])

    const hanldleSalvarCentroCusto = useCallback(async (form: CentroCustoForm) => {

        try {
            setLoading(true);
            setSaving(true);
            setErrorMessage(undefined);

            if (selectedCentroCusto) {
                const newCentroCusto = await updateCentroCustoUseCase.updateCentroCusto(
                    form.idCentroCusto,
                    form.status,
                    form.codigo,
                    form.descricao,
                    form.bdi,
                    form.bdi2,
                    form.numeroCno ?? undefined,
                    form.obsCentroCusto ?? undefined);
                setSelectedCentroCusto(newCentroCusto)

                pushInfoNotification("SUCESSO", "Alteração feita com sucesso!!", { isAutoFinish: true })

            } else {
                const newCentroCusto = await createCentroCustoUseCase.createCentroCusto(
                    form.status,
                    form.codigo,
                    form.descricao,
                    form.bdi,
                    form.bdi2,
                    form.numeroCno ?? undefined,
                    form.obsCentroCusto ?? undefined);

                hookForm.setValue('idCentroCusto', newCentroCusto.idCentroCusto)
                setSelectedCentroCusto(newCentroCusto)
                pushInfoNotification("SUCESSO", "Centro custo criado com sucesso!!", { isAutoFinish: true })

            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao salvar centroCusto"], { isAutoFinish: true })
        } finally {
            setSaving(false);
            setLoading(false);
        }


    }, [
        createCentroCustoUseCase,
        updateCentroCustoUseCase,
        selectedCentroCusto,
        hookForm,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    const handlePageBack = useCallback(() => {
        navigate('/CentroCusto_lista')
    }, [navigate]);


    const handleCentroCustoDeleteOpenState = useCallback((id: number | null, openState: boolean) => {

        if (openState) {
            if (id !== null && id !== 0) {
                setSelectedIdCentroCustoDelete(id)
            } else {
                setSelectedIdCentroCustoDelete(undefined);
                pushErrorNotification("Erro", ["Centro de custo não localizado"], { isAutoFinish: true });
            }
        }
        setOpenCentroCustoDeleteModal(openState);
    }, [pushErrorNotification]);


    const handleConfirmarDeletarCentroCusto = useCallback(async () => {
        try {
            if (selectedIdCentroCustoDelete) {
                setLoading(true);
                const errors = await deleteCentroCustoByIdUseCase.deleteCentroCustoById(selectedIdCentroCustoDelete);

                if (errors.success) {
                    pushInfoNotification("SUCESSO", "Centro de custo deletado com sucesso !!", { isAutoFinish: true })
                } else {

                    if (errors.listaErros !== undefined && errors.listaErros.length > 0) {
                        errors.listaErros.forEach((erro, index) => {
                            pushErrorNotification('Falha', [erro], { isAutoFinish: true })
                        })
                    }
                }
            };
            await handlePageBack();

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao deletar Centro Custo da lista!"], { isAutoFinish: true });

        } finally {
            setLoading(false);
        }
    }, [
        selectedIdCentroCustoDelete,
        deleteCentroCustoByIdUseCase,
        handlePageBack,
        pushInfoNotification,
        pushErrorNotification,
    ]);

    // FUNÇÕES  INFORMÇÃO FATURAMENTO

    const handleGetClienteById = useCallback(async (idCliente: number) => {
        try {
            const cliente = await getClienteByIdUseCase.execute(idCliente);
            return cliente

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao salvar centroCusto"], { isAutoFinish: true })
        }
    }, [getClienteByIdUseCase, pushErrorNotification]);

    const handleInformacaoFaturamentoDeleteOpenState = useCallback((id: number | null, openState: boolean) => {
        if (openState) {
            if (id) {
                setSelectedIdInformacaoFaturamentoDelete(id)
            } else {
                setSelectedIdInformacaoFaturamentoDelete(undefined);
            }
        }
        setOpenInformacaoFaturamentoDeleteModal(openState);
    }, []);

    const handleConfirmarDeletarInformacaoFaturamento = useCallback(async () => {
        try {
            if (selectedIdInformacaoFaturamentoDelete) {
                setLoading(true);
                await deleteInformacaoFaturamentoUseCase.deleteInformacaoFaturamento(selectedIdInformacaoFaturamentoDelete);
                await loadAllInformacaoFaturamento({ id: selectedCentroCusto?.idCentroCusto, page: 1, rowsPerPage: 5 })
                setOpenInformacaoFaturamentoDeleteModal(false)
                pushInfoNotification("SUCESSO", "Deleção realizada com sucesso!!", { isAutoFinish: true });
            };

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao deletar Faturamento!"], { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [
        selectedIdInformacaoFaturamentoDelete,
        deleteInformacaoFaturamentoUseCase,
        selectedCentroCusto,
        loadAllInformacaoFaturamento,
        pushInfoNotification,
        pushErrorNotification,

    ]);


    const handleSalvarInformacaoFaturamento = useCallback(async (form: InformacaoFaturamentoForm) => {

        try {
            setLoading(true);
            setSaving(true);
            setErrorMessage(undefined);

            if (selectedInformacaoFaturamento) {

                const newInformacaoFaturamento = await updateInformacaoFaturamentoUseCase.updateInformacaoFaturamento({
                    idInformacaoFaturamento: form.idInformacaoFaturamento,
                    idCentroCusto: form.idCentroCusto,
                    idEnderecoFaturamento: form.idEnderecoFaturamento,
                    idCliente: form.idCliente,
                    razaoSocial: form.razaoSocial,
                    nomeFantasia: form.nomeFantasia,
                    cnpj: form.cnpj,
                    cpf: form.cpf,
                    inscricaoEstadual: form.inscricaoEstadual,
                    inscricaoMunicipal: form.inscricaoMunicipal,
                    telefoneFixo: form.telefoneFixo,
                    telefoneCelular: form.telefoneCelular,
                    email: form.email,
                    contato: form.contato,
                    principal: form.principal === 1 ? true : false,
                    rua: form.rua ?? "",
                    numero: form.numero ?? "",
                    apartamento: form.apartamento ?? "",
                    cep: form.cep ?? "",
                    uf: form.uf ?? "",
                    cidade: form.cidade ?? "",
                    bairro: form.bairro ?? "",
                    pais: form.pais ?? ""
                });

                await loadAllInformacaoFaturamento({ id: newInformacaoFaturamento.idCentroCusto, rowsPerPage: 5, page: 1 })
                setSelectedInformacaoFaturamento(newInformacaoFaturamento)
                pushInfoNotification("SUCESSO", "Alteração feita com sucesso!!", { isAutoFinish: true })

            } else {

                const newInformacaoFaturamento = await createInformacaoFaturamentoUseCase.createInformacaoFaturamento({
                    idInformacaoFaturamento: 0,
                    idCentroCusto: form.idCentroCusto,
                    idEnderecoFaturamento: 0,
                    idCliente: form.idCliente,
                    razaoSocial: form.razaoSocial,
                    nomeFantasia: form.nomeFantasia,
                    cnpj: form.cnpj,
                    cpf: form.cpf,
                    inscricaoEstadual: form.inscricaoEstadual,
                    inscricaoMunicipal: form.inscricaoMunicipal,
                    telefoneFixo: form.telefoneFixo,
                    telefoneCelular: form.telefoneCelular,
                    email: form.email,
                    contato: form.contato,
                    principal: form.principal === 1 ? true : false,
                    rua: form.rua ?? "",
                    numero: form.numero ?? "",
                    apartamento: form.apartamento ?? "",
                    cep: form.cep ?? "",
                    uf: form.uf ?? "",
                    cidade: form.cidade ?? "",
                    bairro: form.bairro ?? "",
                    pais: form.pais ?? ""
                });

                await loadAllInformacaoFaturamento({ id: newInformacaoFaturamento.idCentroCusto, rowsPerPage: 5, page: 1 })
                hookFormFaturamento.setValue('idInformacaoFaturamento', newInformacaoFaturamento.idInformacaoFaturamento)
                setSelectedInformacaoFaturamento(newInformacaoFaturamento)
                pushInfoNotification("SUCESSO", "Informação faturamento criado com sucesso!!", { isAutoFinish: true })
            }

            setOpenInformacaoFaturamentoModal(false)

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao salvar centroCusto"], { isAutoFinish: true })
        } finally {
            setSaving(false);
            setLoading(false);
        }

    }, [
        createInformacaoFaturamentoUseCase,
        updateInformacaoFaturamentoUseCase,
        selectedInformacaoFaturamento,
        hookFormFaturamento,
        loadAllInformacaoFaturamento,
        pushInfoNotification,
        pushErrorNotification,
    ]);


    const handleInformacaoFaturamentoPageChange = useCallback(async (page: number) => {
        try {
            pagination.informacaoFaturamentosSetter.setPage(page + 1);
            await loadAllInformacaoFaturamento({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar Informacao do Faturamento!"));
        } finally {
            setSelectedInformacaoFaturamento(undefined);
            setOpenInformacaoFaturamentoModal(false);
        }
    }, [
        pagination,
        loadAllInformacaoFaturamento,
        pushErrorNotification,
    ]);

    const handleInformacaoFaturamentoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            pagination.informacaoFaturamentosSetter.setRowsPerPage(rowsPerPage);
            pagination.informacaoFaturamentosSetter.setPage(1);
            await loadAllInformacaoFaturamento({ page: 1, rowsPerPage });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar Informacao do Faturamento!"));
        } finally {
            setSelectedInformacaoFaturamento(undefined);
            setOpenInformacaoFaturamentoModal(false);
        }
    }, [
        pagination,
        loadAllInformacaoFaturamento,
        pushErrorNotification,
    ]);

    const handleGetClientesDropDownList = useCallback(async () => {
        try {
            const listClientes = await getClientesDropDownUseCase.getClientesDropDown();
            setListDropDownClientes(listClientes)

        } catch (e: unknown) {
            pushErrorNotification("ERROR", ["Falha ao buscar lista de clientes para o dropdown."])
        }
    }, [getClientesDropDownUseCase, pushErrorNotification])

    const handleInformacoesFaturamentoModalOpenState = useCallback(async (idFaturamento: number | null, openState: boolean) => {
        if (openState) {
            if (idFaturamento) {

                const faturamento = await getInformacaoFaturamentoUseCase.getInformacaoFaturamento(idFaturamento);
                hookFormFaturamento.setValue('idInformacaoFaturamento', faturamento.idInformacaoFaturamento);
                hookFormFaturamento.setValue('idCentroCusto', faturamento.idCentroCusto);
                hookFormFaturamento.setValue('idEnderecoFaturamento', faturamento.idEnderecoFaturamento);
                hookFormFaturamento.setValue('idCliente', faturamento.idCliente);
                hookFormFaturamento.setValue('razaoSocial', faturamento.razaoSocial ?? '');
                hookFormFaturamento.setValue('nomeFantasia', faturamento.nomeFantasia ?? '');
                hookFormFaturamento.setValue('cnpj', faturamento.cnpj ?? '');
                hookFormFaturamento.setValue('cpf', faturamento.cpf ?? '');
                hookFormFaturamento.setValue('inscricaoEstadual', faturamento.inscricaoEstadual ?? '');
                hookFormFaturamento.setValue('inscricaoMunicipal', faturamento.inscricaoMunicipal ?? '');
                hookFormFaturamento.setValue('telefoneFixo', faturamento.telefoneFixo ?? '');
                hookFormFaturamento.setValue('telefoneCelular', faturamento.telefoneCelular ?? '');
                hookFormFaturamento.setValue('email', faturamento.email ?? '');
                hookFormFaturamento.setValue('contato', faturamento.contato ?? '');
                hookFormFaturamento.setValue('principal', faturamento.principal === true ? 1 : 0);
                //endereco
                hookFormFaturamento.setValue('rua', faturamento.rua ?? "");
                hookFormFaturamento.setValue('numero', faturamento.numero ?? "");
                hookFormFaturamento.setValue('apartamento', faturamento.apartamento ?? "");
                hookFormFaturamento.setValue('cep', faturamento.cep ?? "");
                hookFormFaturamento.setValue('uf', faturamento.uf ?? "");
                hookFormFaturamento.setValue('cidade', faturamento.cidade ?? "");
                hookFormFaturamento.setValue('bairro', faturamento.bairro ?? "");
                hookFormFaturamento.setValue('pais', faturamento.pais ?? "");
                setSelectedInformacaoFaturamento(faturamento);
            } else {
                if (!selectedCentroCusto) {
                    pushErrorNotification("Erro", ["Falha ao buscar Informacao do Faturamento!"]);
                    return
                }
                hookFormFaturamento.reset({
                    principal: 1,
                    idInformacaoFaturamento: 0,
                    idCentroCusto: selectedCentroCusto.idCentroCusto,
                });
                setSelectedInformacaoFaturamento(undefined);
            }
        } else {
            hookFormFaturamento.reset({ principal: 1 });
            setSelectedInformacaoFaturamento(undefined);
        }
        await handleGetClientesDropDownList();
        setOpenInformacaoFaturamentoModal(openState);
    }, [
        getInformacaoFaturamentoUseCase,
        hookFormFaturamento,
        selectedCentroCusto,
        pushErrorNotification,
        handleGetClientesDropDownList,
    ])

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            changeHistory.handleOpenChangeHistory(selectedCentroCusto?.idCentroCusto ?? 0, LogTipoTabela.CENTRO_CUSTO, true);
        }
    }, [
        selectedCentroCusto,
        changeHistory,
    ]);


    const handleLoadCep = useCallback(async (cep: string) => {
        try {
            setErrorMessage(undefined);
            const cepResponse = await getCepUseCase.getCep(cep);
            return cepResponse;
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar CEP!"));
        }
    }, [
        getCepUseCase,
        pushErrorNotification,
    ]);

    // FUNÇÕES ENDEREÇO

    const handleConfirmarDeletarCentroCustoEndereco = useCallback(async () => {
        try {
            if (selectedIdCentroCustoEnderecoDelete) {
                setLoading(true);
                await deleteCentroCustoEnderecoUseCase.deleteCentroCustoEndereco(selectedIdCentroCustoEnderecoDelete);
                await loadAllCentroCustoEndereco({ id: selectedCentroCusto?.idCentroCusto, page: 1, rowsPerPage: 5 })
                setOpenCentroCustoEnderecoDeleteModal(false)
                pushInfoNotification("SUCESSO", "Deleção realizada com sucesso!!", { isAutoFinish: true });
            };

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao deletar Endereço!"], { isAutoFinish: true });
        } finally {
            setLoading(false);
        }
    }, [
        selectedIdCentroCustoEnderecoDelete,
        deleteCentroCustoEnderecoUseCase,
        selectedCentroCusto,
        loadAllCentroCustoEndereco,
        pushInfoNotification,
        pushErrorNotification,
    ]);


    const handleSalvarCentroCustoEndereco = useCallback(async (form: CentroCustoEnderecoForm) => {

        try {
            setLoading(true);
            setSaving(true);
            setErrorMessage(undefined);

            if (selectedCentroCustoEndereco) {

                const newCentroCustoEndereco = await updateCentroCustoEnderecoUseCase.updateCentroCustoEndereco({
                    idCentroCustoEndereco: form.idCentroCustoEndereco,
                    idCentroCusto: form.idCentroCusto,
                    idEndereco: form.idEndereco,
                    tipoEndereco: form.tipoEndereco,

                    // endereço
                    rua: form.rua ?? "",
                    numero: form.numero ?? "",
                    apartamento: form.apartamento ?? "",
                    cep: form.cep ?? "",
                    uf: form.uf ?? "",
                    cidade: form.cidade ?? "",
                    bairro: form.bairro ?? "",
                    pais: form.pais ?? "",
                    principal: form.principal ?? 1,
                });

                await loadAllCentroCustoEndereco({ id: newCentroCustoEndereco.idCentroCusto, rowsPerPage: 5, page: 1 })
                setSelectedCentroCustoEndereco(newCentroCustoEndereco)

            } else {

                const newCentroCustoEndereco = await createCentroCustoEnderecoUseCase.createCentroCustoEndereco({
                    idCentroCustoEndereco: 0,
                    idCentroCusto: form.idCentroCusto,
                    idEndereco: 0,
                    tipoEndereco: form.tipoEndereco ?? "",

                    // endereço
                    rua: form.rua ?? "",
                    numero: form.numero ?? "",
                    apartamento: form.apartamento ?? "",
                    cep: form.cep ?? "",
                    uf: form.uf ?? "",
                    cidade: form.cidade ?? "",
                    bairro: form.bairro ?? "",
                    pais: form.pais ?? "",
                    principal: form.principal ?? 1

                });

                await loadAllCentroCustoEndereco({ id: newCentroCustoEndereco.idCentroCusto, rowsPerPage: 5, page: 1 })
                hookFormEndereco.setValue('idCentroCustoEndereco', newCentroCustoEndereco.idCentroCustoEndereco ?? 0)
                setSelectedCentroCustoEndereco(newCentroCustoEndereco)
            }
            setOpenCentroCustoEnderecoModal(false)
            pushInfoNotification("SUCESSO", "Endereço criado com sucesso!!", { isAutoFinish: true })

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao salvar Endereço!"], { isAutoFinish: true })
        } finally {
            setSaving(false);
            setLoading(false);
        }

    }, [
        hookFormEndereco,
        createCentroCustoEnderecoUseCase,
        updateCentroCustoEnderecoUseCase,
        selectedCentroCustoEndereco,
        loadAllCentroCustoEndereco,
        pushErrorNotification,
        pushInfoNotification,
    ]);


    const handleCentroCustoEnderecoPageChange = useCallback(async (page: number) => {
        try {
            pagination.centroCustoEnderecosSetter.setPage(page + 1);
            await loadAllCentroCustoEndereco({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar centro de custo!"));
        } finally {
            setSelectedCentroCustoEndereco(undefined);
            setOpenCentroCustoEnderecoModal(false);
        }
    }, [
        pagination,
        loadAllCentroCustoEndereco,
        pushErrorNotification,
    ]);

    const handleCentroCustoEnderecoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            pagination.centroCustoEnderecosSetter.setRowsPerPage(rowsPerPage);
            pagination.centroCustoEnderecosSetter.setPage(1);
            await loadAllCentroCustoEndereco({ page: 1, rowsPerPage });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar Endereço!"));
        } finally {
            setSelectedCentroCustoEndereco(undefined);
            setOpenCentroCustoEnderecoModal(false);
        }
    }, [
        pagination,
        loadAllCentroCustoEndereco,
        pushErrorNotification,
    ]);

    const handleCentroCustoEnderecoModalOpenState = useCallback(async (idCentroCustoEndereco: number | null, openState: boolean) => {
        if (openState) {
            if (idCentroCustoEndereco) {
                const endereco = await getCentroCustoEnderecoUseCase.getCentroCustoEnderecoByid(idCentroCustoEndereco);
                hookFormEndereco.setValue('idCentroCustoEndereco', endereco.idCentroCustoEndereco ?? 0);
                hookFormEndereco.setValue('idCentroCusto', endereco.idCentroCusto ?? 0);
                hookFormEndereco.setValue('idEndereco', endereco.idEndereco);

                //endereco
                hookFormEndereco.setValue('rua', endereco.rua ?? "");
                hookFormEndereco.setValue('numero', endereco.numero ?? "");
                hookFormEndereco.setValue('apartamento', endereco.apartamento ?? "");
                hookFormEndereco.setValue('cep', endereco.cep ?? "");
                hookFormEndereco.setValue('uf', endereco.uf ?? "");
                hookFormEndereco.setValue('cidade', endereco.cidade ?? "");
                hookFormEndereco.setValue('bairro', endereco.bairro ?? "");
                hookFormEndereco.setValue('pais', endereco.pais ?? "");
                hookFormEndereco.setValue('principal', endereco.principal ?? 1);
                setSelectedCentroCustoEndereco(endereco);
            } else {
                if (!selectedCentroCusto) {
                    pushErrorNotification("Erro", ["Falha ao buscar Endereço!"]);
                    return
                }
                hookFormEndereco.reset({
                    idCentroCustoEndereco: 0,
                    idCentroCusto: selectedCentroCusto.idCentroCusto,
                    principal: 1,
                });
                setSelectedCentroCustoEndereco(undefined);
            }
        } else {
            hookFormEndereco.reset({
                principal: 1,
            });
            setSelectedCentroCustoEndereco(undefined);
        }
        setOpenCentroCustoEnderecoModal(openState);
    }, [
        getCentroCustoEnderecoUseCase,
        hookFormEndereco,
        selectedCentroCusto,
        pushErrorNotification,
    ])

    const handleCentroCustoEnderecoDeleteOpenState = useCallback((id: number | null, openState: boolean) => {
        if (openState) {
            if (id) {
                setselectedIdCentroCustoEnderecoDelete(id)
            } else {
                setselectedIdCentroCustoEnderecoDelete(undefined);
            }
        }
        setOpenCentroCustoEnderecoDeleteModal(openState);
    }, []);


    return {
        saving,
        changeHistory,
        loading,
        hookForm,
        hookFormFaturamento,
        selectedCentroCusto,
        selectedInformacaoFaturamento,
        openInformacaoFaturamentoModal,
        listDropDownClientes,
        selectedClienteDropDown,
        openInformacaoFaturamentoDeleteModal,
        openCentroCustoDeleteModal,
        openCentroCustoEnderecoDeleteModal,
        informacaoFaturamentolist,
        pagination,
        debouncedRef,
        openMenu,
        menuRef,
        hookFormEndereco,
        selectedCentroCustoEndereco,
        openCentroCustoEnderecoModal,
        centroCustoEnderecoList,
        handleConfirmarDeletarCentroCustoEndereco,
        handleCentroCustoEnderecoModalOpenState,
        handleCentroCustoEnderecoDeleteOpenState,
        handleCentroCustoEnderecoPageChange,
        handleCentroCustoEnderecoRowsPerPageChange,
        handleSalvarCentroCustoEndereco,
        handleCentroCustoDeleteOpenState,
        handleCloseMenu,
        handleListKeyDown,
        handleToggleMenu,
        handleLoadCep,
        handleNewCentroCusto,
        handleInformacaoFaturamentoPageChange,
        handleInformacaoFaturamentoRowsPerPageChange,
        handleInformacoesFaturamentoModalOpenState,
        handleOpenChangeHistory,
        handlePageBack,
        hanldleSalvarCentroCusto: hookForm.handleSubmit(hanldleSalvarCentroCusto),
        handleSalvarInformacaoFaturamento,
        setOpenInformacaoFaturamentoDeleteModal,
        setOpenCentroCustoDeleteModal,
        handleGetClienteById,
        handleConfirmarDeletarCentroCusto,
        handleConfirmarDeletarInformacaoFaturamento,
        handleInformacaoFaturamentoDeleteOpenState,


    }
}